import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CustomerService, CustomerData } from '../../services/customer.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';
import {
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
} from '../../shared/validators/customer-contact-field.rules';
import { Subject } from 'rxjs';
import {
  runCustomerPhoneSearch,
  runCustomerNameSearch,
  clearCustomerSearchDropdowns,
  patchCustomerFormFromSearchResult,
  setupCustomerSearchSubscriptions,
} from '../../shared/utils/customer-search.util';
import { hideDropdownAfterDelay } from '../../shared/utils/dropdown.util';

interface CustomerFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

/**
 * CustomerInfoComponent - manages a reactive customer information form (first name, last name, phone, email) with live validation, name/phone search, and create-customer flows.
 * @component
 * @selector app-customer-info
 * @example
 *   <app-customer-info
 *     [initialData]="sampleCustomer"
 *     (formValidityChange)="onValidity($event)"
 *     (formDataChange)="onData($event)"
 *     (customerCreated)="onCreated($event)"
 *     (processingStateChange)="onProcessing($event)">
 *   </app-customer-info>
 * @input {CustomerFormData} initialData - Initial values to pre-populate the reactive form (firstName, lastName, phoneNumber, email).
 * @output {EventEmitter<boolean>} formValidityChange - Emits the current form validity whenever it changes and on initialization.
 * @output {EventEmitter<CustomerFormData>} formDataChange - Emits the current form value object whenever the form changes and on initialization.
 * @output {EventEmitter<any>} customerCreated - Emitted when a customer is created or selected from search; payload contains the created/selected customer data or error-derived customer info. Search selections include `selectedFromSearch: true` so parents can avoid auto-advancing steps.
 * @output {EventEmitter<boolean>} processingStateChange - Emits processing state (true while creating a customer, false when finished) to drive loading UI.
 *
 * Key methods:
 * - createCustomer(): Validates the form and, if valid, maps form values to backend CustomerData, calls CustomerService.createCustomer, handles success and error cases (including duplicate-customer handling), toggles isCreatingCustomer, and emits customerCreated and processingStateChange.
 * - onFirstNameInput(event): Triggers debounced name search when first name input length >= 2; updates name search results and dropdown visibility.
 * - onPhoneInput(event): Triggers debounced phone search when phone input length >= 3; updates phone search results and dropdown visibility.
 * - searchCustomersByName(name): Invokes shared search helper to perform name search via CustomerService and populate nameSearchResults.
 * - searchCustomersByPhone(phoneNumber): Invokes shared search helper to perform phone search via CustomerService and populate phoneSearchResults.
 * - selectCustomerFromSearch(customer): Patches the form with selected customer data, marks fields touched to trigger validation, emits form data/validity and customerCreated (with selectedFromSearch: true), and clears search dropdowns.
 * - getFieldError(fieldName): Returns a user-facing validation message for a given control based on validators (required, maxlength, strictEmail, usaPhone, standardName).
 * - onFieldKeyup(fieldName): Marks a field as touched and updates its validation state (used to surface validation messages on keyup).
 * - hideNameDropdown() / hidePhoneDropdown(): UI helpers to hide dropdowns after a short delay or on outside click.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Builds the reactive FormGroup with appropriate validators, sets up debounced search subscriptions, subscribes to statusChanges/valueChanges to emit formValidityChange and formDataChange, and emits initial form validity and data.
 * - ngOnChanges(changes: SimpleChanges): Watches for changes to initialData and patches the form without emitting value change events when initialData is updated externally.
 * - ngOnDestroy(): Cleans up subscription streams (destroy$) to prevent memory leaks and cancel ongoing searches.
 *
 * Notes:
 * - Uses injected services: FormBuilder for form creation, CustomerService for searching/creating customers, and NotificationService for user messaging.
 * - Integrates with shared helper utilities for debounced searches and dropdown/patch helpers (setupCustomerSearchSubscriptions, runCustomerNameSearch, runCustomerPhoneSearch, patchCustomerFormFromSearchResult, clearCustomerSearchDropdowns, hideDropdownAfterDelay).
 */
@Component({
  selector: 'app-customer-info',
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './customer-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './customer-info.component.scss',
})
export class CustomerInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Output() formDataChange = new EventEmitter<CustomerFormData>();
  @Output() customerCreated = new EventEmitter<any>();
  @Output() processingStateChange = new EventEmitter<boolean>();
  @Input() initialData: CustomerFormData = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  };

  customerForm!: FormGroup;
  isCreatingCustomer = false;

  // Name search properties
  nameSearchResults: any[] = [];
  showNameDropdown = false;
  isSearchingCustomersByName = false;
  private nameSearchSubject = new Subject<string>();

  // Phone search properties
  phoneSearchResults: any[] = [];
  showPhoneDropdown = false;
  isSearchingCustomers = false;
  readonly phoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  private phoneSearchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private messageService: NotificationService
  ) {}

  /**
   * Initialize the customer reactive form with validation, set up name search subscriptions, and emit form validity and data changes (including initial values).
   * @example
   * gOnInit()
   * undefined
   * @returns {{void}} Sets up the form controls, validators, subscriptions, and emits initial form validity and data.
   **/
  ngOnInit() {
    this.customerForm = this.fb.group({
      firstName: [
        this.initialData.firstName,
        [Validators.required, Validators.maxLength(100), standardNameValidator()],
      ],
      lastName: [
        this.initialData.lastName,
        [Validators.required, Validators.maxLength(100), standardNameValidator()],
      ],
      phoneNumber: [
        this.initialData.phoneNumber,
        [Validators.required, Validators.maxLength(CUSTOMER_PHONE_MAX_LENGTH), usaPhoneValidator()],
      ],
      email: [this.initialData.email, [Validators.maxLength(254), strictEmailValidator()]],
    });

    // Setup name search subscription
    this.setupSearchSubscriptions();

    // Emit form validity changes
    this.customerForm.statusChanges.subscribe(() => {
      this.formValidityChange.emit(this.customerForm.valid);
    });

    // Emit form data changes
    this.customerForm.valueChanges.subscribe(value => {
      this.formDataChange.emit(value);
    });

    // Emit initial validity and data
    this.formValidityChange.emit(this.customerForm.valid);
    this.formDataChange.emit(this.customerForm.value);
  }

  private setupSearchSubscriptions(): void {
    setupCustomerSearchSubscriptions(
      this.nameSearchSubject,
      this.phoneSearchSubject,
      this.destroy$,
      name => this.searchCustomersByName(name),
      phoneNumber => this.searchCustomersByPhone(phoneNumber)
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialData'] && this.customerForm) {
      // Update form values when initialData changes
      this.customerForm.patchValue(this.initialData, { emitEvent: false });
    }
  }

  /**
   * Returns a user-friendly validation error message for the specified form field if it has validation errors and has been touched, otherwise returns an empty string.
   * @example
   * etFieldError('email')
   * "Email is required"
   * @param {string} fieldName - The name of the form control to check for validation errors.
   * @returns {string} The validation error message to display, or an empty string if no error should be shown.
   */
  getFieldError(fieldName: string): string {
    const field = this.customerForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['maxlength']) {
        const maxLength = field.errors['maxlength'].requiredLength;
        return `${this.getFieldLabel(fieldName)} cannot exceed ${maxLength} characters`;
      }
      if (field.errors['strictEmail']) return field.errors['strictEmail'].message;
      if (field.errors['usaPhone']) return field.errors['usaPhone'].message;
      if (field.errors['standardName']) return field.errors['standardName'].message;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email',
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * Create a new customer from the component's reactive form, call the customer service to persist it, and emit lifecycle events for processing state and created customer.
   * @example
   * createCustomer()
   * void
   * @returns {void} No direct return value. Side effects: emits processingStateChange(true/false), toggles isCreatingCustomer, and emits customerCreated with the created or existing customer data.
   */
  createCustomer(): void {
    if (this.customerForm.valid && !this.isCreatingCustomer) {
      this.isCreatingCustomer = true;
      this.processingStateChange.emit(true);

      const formData = this.customerForm.value;

      // Map form data to CustomerData interface
      const customerData: CustomerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phoneNumber,
        email: formData.email,
        customerType: 'regular', // Default customer type
        licensePlate: '', // Will be added in vehicle step
      };

      this.customerService.createCustomer(customerData, false).subscribe({
        next: response => {
          this.isCreatingCustomer = false;
          this.processingStateChange.emit(false);

          this.customerCreated.emit(response);
        },
        error: error => {
          this.isCreatingCustomer = false;
          this.processingStateChange.emit(false);

          // Handle duplicate customer error - extract customer info from error response
          if (error.status === 409 || error.error?.message?.includes('already exists')) {
            this.customerCreated.emit(error.error);
          }
        },
      });
    }
  }

  onFieldKeyup(fieldName: string) {
    const fieldControl = this.customerForm.get(fieldName);
    if (fieldControl) {
      fieldControl.markAsTouched();
      fieldControl.updateValueAndValidity();
    }
  }

  /**
   * Handles input events for the first name field: trims the value, triggers a name search when the value has at least 2 characters, otherwise clears search results and hides the dropdown.
   * @example
   * nFirstNameInput(event)
   * undefined
   * @param {Event} event - Input event from the first name input element.
   * @returns {void} No return value.
   **/
  onFirstNameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();

    // Trigger search if value is at least 2 characters
    if (value.length >= 2) {
      this.nameSearchSubject.next(value);
    } else {
      this.nameSearchResults = [];
      this.showNameDropdown = false;
    }
  }

  searchCustomersByName(name: string): void {
    runCustomerNameSearch(name, this.customerService, this.destroy$, this);
  }

  /**
   * Handle phone input: strip non-phone chars, cap at 10 digits, then search when length >= 3.
   * @example
   * nPhoneInput(new Event('input'))
   * undefined
   * @param {{Event}} {{event}} - The input event from the phone number input element.
   * @returns {{void}} No return value; updates component state and emits search queries via a subject.
   **/
  onPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const sanitized = sanitizeCustomerPhoneInput(target.value);
    if (target.value !== sanitized) {
      target.value = sanitized;
    }
    const control = this.customerForm.get('phoneNumber');
    if (control && control.value !== sanitized) {
      control.setValue(sanitized, { emitEvent: true });
    }

    const value = sanitized.trim();

    // Trigger search if value is at least 3 characters
    if (value.length >= 3) {
      this.phoneSearchSubject.next(value);
    } else {
      this.phoneSearchResults = [];
      this.showPhoneDropdown = false;
    }
  }

  searchCustomersByPhone(phoneNumber: string): void {
    runCustomerPhoneSearch(phoneNumber, this.customerService, this.destroy$, this);
  }

  /**
   * Select customer from search results
   */
  selectCustomerFromSearch(customer: any): void {
    patchCustomerFormFromSearchResult(this.customerForm, customer, 'phoneNumber');

    // Mark all fields as touched to trigger validation
    Object.keys(this.customerForm.controls).forEach(key => {
      this.customerForm.get(key)?.markAsTouched();
      this.customerForm.get(key)?.updateValueAndValidity();
    });

    if (this.customerForm.valid) {
      this.formDataChange.emit(this.customerForm.value);
      this.formValidityChange.emit(true);
    }

    this.customerCreated.emit({
      status: true,
      selectedFromSearch: true,
      data: {
        id: customer.id || customer.customer_id || customer.customerId,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        contact: customer.contact,
      },
    });

    clearCustomerSearchDropdowns(this);
  }

  /**
   * Hide name dropdown when clicking outside
   */
  hideNameDropdown(): void {
    setTimeout(() => {
      this.showNameDropdown = false;
    }, 200);
  }

  /**
   * Hide phone dropdown when clicking outside
   */
  hidePhoneDropdown(): void {
    hideDropdownAfterDelay(() => {
      this.showPhoneDropdown = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
